import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Card } from "../../../components/ui/Card";
import { formatCurrency } from "../../../lib/utils";

interface VentasVsGastosProps {
  data: any[];
  isLoading: boolean;
  periodo?: number;
  onPeriodoChange?: (meses: 6 | 12) => void;
}

export function VentasVsGastos({ data, isLoading, periodo = 6, onPeriodoChange }: VentasVsGastosProps) {
  
  if (isLoading) {
    return (
      <Card className="p-4 h-96 flex items-center justify-center">
        <div className="text-gray-500">Cargando gráfico...</div>
      </Card>
    );
  }

  // Cálculos para los totales de las tarjetas
  const totalVentas = data.reduce((sum, item) => sum + (item.ventas || 0), 0);
  const totalGastos = data.reduce((sum, item) => sum + (item.gastos || 0), 0);
  const diferencia = totalVentas - totalGastos;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Ventas vs Gastos</h3>
          <p className="text-sm text-gray-500">Comparativa mensual</p>
        </div>
        
        {/* Selector de periodo */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => onPeriodoChange && onPeriodoChange(6)}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
              periodo === 6
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            6 meses
          </button>
          <button
            onClick={() => onPeriodoChange && onPeriodoChange(12)}
            className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
              periodo === 12
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            12 meses
          </button>
        </div>
      </div>

      {/* Tarjetas de Resumen (Ventas, Gastos, Diferencia) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
          <p className="text-xs text-gray-500 mb-1">Total Ventas</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(totalVentas)}</p>
        </div>
        <div className="bg-red-50/50 p-4 rounded-lg border border-red-100">
          <p className="text-xs text-gray-500 mb-1">Total Gastos</p>
          <p className="text-lg font-bold text-red-600">{formatCurrency(totalGastos)}</p>
        </div>
        <div className="bg-green-50/50 p-4 rounded-lg border border-green-100">
          <p className="text-xs text-gray-500 mb-1">Diferencia</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(diferencia)}</p>
        </div>
      </div>

      {/* Gráfico */}
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={true} stroke="#f0f0f0" />
            <XAxis 
              dataKey="mes" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              dy={10}
            />
            <YAxis 
              tickFormatter={(value) => `$${value / 1000}K`}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              dx={-10}
            />
            <Tooltip
              formatter={(value) => typeof value === 'number' ? formatCurrency(value) : value}
              cursor={{ fill: '#f3f4f6' }}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            />
            <Legend 
              iconType="square" 
              wrapperStyle={{ paddingTop: '20px' }} 
            />
            <Bar dataKey="gastos" name="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={50} />
            <Bar dataKey="ventas" name="Ventas" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={50} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}